import { ComponentPropsWithRef, forwardRef, ReactElement, ReactNode } from 'react';

import FormControl, {
  ErrorText,
  FormControlProps,
  HelperText,
  Label,
  WarningText,
} from 'design-system/components/FormControl';

export interface FormFieldBaseProps {
  /**
   * Renders `<Label>` above input
   */
  label?: ReactNode;

  /**
   * Renders `<HelperText>` below input
   */
  helperText?: ReactNode;

  /**
   * Renders `<ErrorText>` below input and helper text.
   */
  errorText?: ReactNode;

  /**
   * Renders `<WarningText>` below input and helper text.
   */
  warningText?: ReactNode;

  /**
   * Should be an input of some sort
   */
  children?: ReactElement;

  labelProps?: ComponentPropsWithRef<typeof Label>;
  helperProps?: ComponentPropsWithRef<typeof HelperText>;
  warningProps?: ComponentPropsWithRef<typeof WarningText>;
  errorProps?: ComponentPropsWithRef<typeof ErrorText>;
}

export interface FormFieldProps extends Omit<FormControlProps, 'children'>, FormFieldBaseProps {}

/**
 * > **Please do not use in production! This component is subject to API and style changes.**
 *
 * Helper components with preset slots for label, input, and helper texts.
 * Fully composed components with different form input types are available.
 * This is the recommended way to implement form elements.
 * You can use `<FormControl>` if you require more flexibility.
 *
 * ## Usage
 *
 * ```tsx
 * import FormField from 'design-system/components/FormField';
 * ```
 */
const FormField = forwardRef<HTMLDivElement, FormFieldProps>(function FormField(
  {
    label,
    children,
    helperText,
    warningText,
    errorText,
    labelProps,
    helperProps,
    warningProps,
    errorProps,
    ...props
  }: FormFieldProps,
  ref,
) {
  return (
    <FormControl hasError={!!errorText} hasWarning={!!warningText} ref={ref} {...props}>
      <Label {...labelProps}>{label}</Label>
      {children}
      {helperText && <HelperText {...helperProps}>{helperText}</HelperText>}
      {warningText && <WarningText {...warningProps}>{warningText}</WarningText>}
      {errorText && <ErrorText {...errorProps}>{errorText}</ErrorText>}
    </FormControl>
  );
});

export default FormField;
